/**
 * What the graph looks like: the scene and what the user is doing, turned into a list of
 * {@link Shape}s (a {@link Picture}). Ported from the prototype: shapes per node kind, colour per
 * target, link styles per kind, dimming around the focused node, labels, handles, and the dashed
 * preview while linking or reconnecting. Groups are transparent circles drawn first, outermost
 * first; their names, on their rims, go over everything else so they stay readable and
 * clickable. Links between groups can be bundled: one thick link with a count per pair of
 * groups, opened into its links when the user looks at it (see `Focus` in the scene).
 *
 * In the 3D view the scene already gives projected positions and sizes; the picture only adds
 * the depth cues: nodes are painted farthest first, and fade (with their links and names) the
 * farther they are.
 *
 * Big projects (tens of thousands of nodes) stay quick because the picture only holds what can
 * be seen: what is off screen is left out (with a margin, so nothing pops in at the edges), and
 * details too small to make out are too: arrow heads on links a few pixels long, comment dots on
 * tiny nodes. Names are drawn up to {@link LABEL_BUDGET}, the most important first (what is
 * focused or searched, then the most linked nodes), never one over another: zoomed out on 40 000
 * nodes, a few hundred readable names say more than a grey cloud of text. The kinds' looks are
 * looked up once per kind for each picture.
 *
 * Nothing here draws: a painter does (`paint2d.ts`, or `gl/painter.ts` on the graphics card).
 */
import type { Kinds } from "@protocol/Kinds";
import type { Link } from "@protocol/Link";
import { linkKindOf, nodeKindOf, type LinkKindView, type NodeKindView } from "../../shared/kinds";
import { matchesQuery } from "../../shared/analysis";
import type { Focus, Scene, SceneBundle, SceneGroup, SimLink, SimNode } from "../../state/scene";
import { sameCategory } from "../../shared/categories";
import type { Palette } from "./palette";
import { both, filled, stroked, type FlatShape, type ImageShape, type Picture, type Shape } from "./shapes";
import { pictureOf } from "../../shared/assets";
import { sameEdge } from "../../client/graph";
import { COUNT_CELL_PX, DIMMED_ALPHA, DOT_MIN_PX, DOTTED_DASH, HEAD_MIN_PX, LABEL_BUDGET, LABEL_CELL_PX, LINK_END_PX, OUT_OF_FOCUS_ALPHA, PICTURE_MIN_PX, RING_GAP_PX, SCREEN_MARGIN_PX, SMALL_TEXT_PX } from "./constants";
import { COUNTS_ALWAYS, COUNT_BUDGET, COUNT_MIN_PX } from "../../state/constants";

/** A link being drawn from a node towards the pointer (or a target node). */
export interface Linking {
  from: SimNode;
  x: number;
  y: number;
  to: SimNode | null;
}

/** An end of the selected link being dragged to another node. */
export interface Reconnecting {
  link: SimLink;
  end: "a" | "b";
  x: number;
  y: number;
  to: SimNode | null;
}

/** Everything besides the scene that affects the picture. */
export interface DrawState {
  palette: Palette;
  selectedNode: SimNode | null;
  selectedLink: SimLink | null;
  hoverNode: SimNode | null;
  hoverLink: SimLink | null;
  selectedGroup: SceneGroup | null;
  /** What decides which bundles are open. */
  lookAt: Focus;
  hoverBundle: SceneBundle | null;
  hoverGroup: SceneGroup | null;
  query: string;
  labels: boolean;
  linking: Linking | null;
  reconnecting: Reconnecting | null;
  /** Hide the link handles (while a node is being dragged). */
  hideHandles: boolean;
  /** Every kind (known, the graph's, the plugins'), whose shapes, lines and heads nodes and links take (none: the plain look). */
  kinds?: Kinds;
  /** Pages mentioning other nodes (`[[Name]]`): faint dotted lines, under the links (none: not drawn). */
  mentions?: readonly { from: string; to: string }[];
}

/** A line's ends on screen. */
type Ends = { x1: number; y1: number; x2: number; y2: number };

/** Screen position of the "drag to link" handle beside a node. */
export function handlePosition(scene: Scene, n: SimNode): { x: number; y: number } {
  return { x: scene.sx(n) + scene.rs(n) + 11, y: scene.sy(n) };
}

/** `true` if `link` is the link `l` refers to. */
export function isLink(l: SimLink | null, link: Link | null): boolean {
  return !!l && !!link && sameEdge(l.link, link);
}

/** Colour of a node: its first category's colour, from the graph's order of categories. */
function colorOf(scene: Scene, p: Palette, n: SimNode): string {
  const first = n.node.categories[0];
  const at = first === undefined ? -1 : scene.categories.findIndex((c) => sameCategory(c, first));
  return at < 0 ? p.noCategory : p.categories[at % p.categories.length]!;
}

/** The whole picture of the scene. */
export function picture(scene: Scene, s: DrawState): Picture {
  const p = s.palette;
  const base: FlatShape[] = [];
  const top: Shape[] = [];
  // Each kind's look once per picture (looked up for every node and link otherwise).
  const nodeLooks = new Map<string, NodeKindView>();
  const linkLooks = new Map<string, LinkKindView>();
  const nodeLook = (n: SimNode): NodeKindView => {
    let look = nodeLooks.get(n.node.kind);
    if (!look) nodeLooks.set(n.node.kind, (look = nodeKindOf(s.kinds, n.node)));
    return look;
  };
  const linkLook = (l: SimLink): LinkKindView => {
    let look = linkLooks.get(l.link.kind);
    if (!look) linkLooks.set(l.link.kind, (look = linkKindOf(s.kinds, l.link)));
    return look;
  };
  /** A bundle's look: its one kind's (`null` when it mixes kinds). */
  const bundleLook = (b: SceneBundle): LinkKindView | null => {
    const kind = b.kinds.length === 1 ? b.kinds[0]! : null;
    if (kind === null) return null;
    let look = linkLooks.get(kind);
    if (!look) linkLooks.set(kind, (look = linkKindOf(s.kinds, { kind })));
    return look;
  };
  const [right, bottom] = [scene.width + SCREEN_MARGIN_PX, scene.height + SCREEN_MARGIN_PX];
  /** Whether a circle on screen is (partly) within the canvas and its margin. */
  const circleShown = (x: number, y: number, r: number) => x + r > -SCREEN_MARGIN_PX && x - r < right && y + r > -SCREEN_MARGIN_PX && y - r < bottom;
  /** Whether a node is drawn: on the canvas (its name, under it, counts too). */
  const nodeShown = (n: SimNode) => circleShown(scene.sx(n), scene.sy(n) + 8, scene.rs(n) + 16);
  /** Whether a segment's box meets the canvas and its margin. */
  const segmentShown = (seg: Ends) => Math.max(seg.x1, seg.x2) > -SCREEN_MARGIN_PX && Math.min(seg.x1, seg.x2) < right && Math.max(seg.y1, seg.y2) > -SCREEN_MARGIN_PX && Math.min(seg.y1, seg.y2) < bottom;

  const focus = s.hoverNode ?? s.selectedNode;
  let neighbours: Set<SimNode> | null = null;
  if (focus) {
    neighbours = new Set([focus]);
    for (const l of scene.links) if (l.visible && (l.a === focus || l.b === focus)) neighbours.add(l.a).add(l.b);
  } else if (s.selectedLink) {
    neighbours = new Set([s.selectedLink.a, s.selectedLink.b]);
  } else if (s.lookAt.bundle) {
    neighbours = new Set(s.lookAt.bundle.links.flatMap((l) => [l.a, l.b]));
  } else if (s.selectedGroup) {
    neighbours = new Set(s.selectedGroup.all);
  }
  const selectedBundle = s.lookAt.bundle;
  const q = s.query;
  /** `true` when the node matches the search. */
  const match = (n: SimNode) => !q || matchesQuery(n.node, q);
  /** `true` when the node is drawn faded (not near the focus, or not matching). */
  const dimmed = (n: SimNode) => (!!neighbours && !neighbours.has(n)) || (!!q && !match(n));

  scene.layoutGroups();
  const groups = scene.groups.filter((g) => g.visible).sort((a, b) => a.depth - b.depth);
  const looks = new Map(groups.map((g) => [g, groupLook(s, g, !!focus || !!s.selectedLink)]));
  for (const g of groups) {
    const circle = scene.groupCircle(g);
    // Off screen, or so big that the canvas is inside it with its rim out of sight.
    const away = Math.hypot(circle.x - scene.width / 2, circle.y - scene.height / 2);
    const corner = Math.hypot(scene.width, scene.height) / 2 + SCREEN_MARGIN_PX;
    if (away > circle.r + corner || circle.r > away + corner) {
      if (circleShown(circle.x, circle.y, circle.r)) groupShapes(base, circle, g.depth, looks.get(g)!, true);
      continue;
    }
    groupShapes(base, circle, g.depth, looks.get(g)!);
  }

  // Mentions, like a notes vault's graph: centre to centre, under the nodes; where a link already joins the two, the link says enough.
  if (s.mentions?.length) {
    const linked = new Set(scene.links.flatMap((l) => [`${l.a.node.id} ${l.b.node.id}`, `${l.b.node.id} ${l.a.node.id}`]));
    for (const m of s.mentions) {
      const a = scene.byId.get(m.from);
      const b = scene.byId.get(m.to);
      if (!a?.visible || !b?.visible || linked.has(`${m.from} ${m.to}`)) continue;
      if (!segmentShown({ x1: scene.sx(a), y1: scene.sy(a), x2: scene.sx(b), y2: scene.sy(b) })) continue;
      const near = !!focus && (a === focus || b === focus);
      const alpha = (focus ? (near ? 0.75 : 0.05) : 0.4) * Math.min(scene.fade(a), scene.fade(b));
      base.push({ type: "segment", x1: scene.sx(a), y1: scene.sy(a), x2: scene.sx(b), y2: scene.sy(b), round: false, paint: stroked(p.muted, 1, alpha, [...DOTTED_DASH]) });
    }
  }

  for (const l of scene.links) {
    if (!l.visible || !scene.linkDrawn(l, s.lookAt)) continue;
    let touch = !!focus && (l.a === focus || l.b === focus);
    let alpha = 0.85;
    if (focus && !touch) alpha = OUT_OF_FOCUS_ALPHA;
    else if (q && !(match(l.a) || match(l.b))) alpha = OUT_OF_FOCUS_ALPHA;
    if (selectedBundle && !focus) {
      touch = scene.bundleOf.get(l) === selectedBundle;
      alpha = touch ? 1 : OUT_OF_FOCUS_ALPHA;
    }
    if (s.selectedLink) {
      if (l === s.selectedLink) alpha = 1;
      else if (!focus) alpha = OUT_OF_FOCUS_ALPHA;
    }
    if (l === s.hoverLink && alpha < 1) {
      alpha = 1;
      touch = true;
    }
    const seg = scene.segment(l);
    if (!seg || !segmentShown(seg)) continue;
    if (l === s.selectedLink) {
      // A soft glow under the selected link.
      base.push({ type: "segment", ...seg, round: true, paint: stroked(p.accent, 7, 0.28) });
      touch = true;
    }
    linkShapes(base, p, seg, linkLook(l), alpha * Math.min(scene.fade(l.a), scene.fade(l.b)), touch);
  }

  const otherFocus = !!focus || !!s.selectedLink || !!selectedBundle || !!q;
  const closed = scene.bundles.flatMap((b) => {
    const seg = scene.isOpen(b, s.lookAt) ? null : scene.bundleSegment(b);
    return seg && segmentShown(seg) ? [{ b, seg, alpha: b === s.hoverBundle ? 1 : otherFocus ? 0.12 : 0.8 }] : [];
  });
  for (const { b, seg, alpha } of closed) bundleShapes(base, p, seg, b, alpha, bundleLook);

  const order = scene.paintOrder().filter(nodeShown);
  for (const n of order) {
    let ring: string | null = null;
    if (n === s.selectedNode || (n.fold && s.selectedGroup?.id === n.fold.group.id)) ring = p.fg;
    else if (n === s.hoverNode) ring = p.muted;
    else if (q && match(n)) ring = p.accent;
    const alpha = (dimmed(n) ? DIMMED_ALPHA : 1) * scene.fade(n);
    if (n.fold) foldShapes(base, top, scene, p, n, alpha, ring);
    else nodeShapes(base, scene, p, n, alpha, ring, nodeLook(n));
  }
  // The nodes' pictures, over their shapes (on Canvas 2D: the graphics card draws no images).
  for (const n of order) {
    const shape = n.node.attachments?.length ? pictureShape(scene, n, (dimmed(n) ? DIMMED_ALPHA : 1) * scene.fade(n), s.kinds) : null;
    if (shape) top.push(shape);
  }

  if (s.labels) {
    /** A name to draw: its node, how much it matters (higher first) and how it looks. */
    const wanted: { n: SimNode; rank: number; dim: boolean; near: boolean; special: boolean }[] = [];
    for (const n of order) {
      const d = dimmed(n);
      const near = !!neighbours && neighbours.has(n) && !d;
      const special = n === s.selectedNode || n === s.hoverNode || near || (!!q && match(n));
      // In 3D, only the names of the front half are drawn: the back would be a cloud of text.
      const show = (scene.view.k * scene.depthScale(n) > 0.62 && scene.fade(n) > 0.9) || special;
      if (show) wanted.push({ n, rank: (special ? 1e6 : 0) + n.degree, dim: d, near, special });
    }
    // The most important names first; one name per cell of the grid, up to the budget (what is
    // focused or searched is always drawn).
    wanted.sort((a, b) => b.rank - a.rank);
    const taken = new Set<number>();
    let drawn = 0;
    for (const { n, dim, near, special } of wanted) {
      if (drawn >= LABEL_BUDGET && !special) break;
      const x = scene.sx(n);
      const y = scene.sy(n) + scene.rs(n);
      const cell = Math.floor(x / LABEL_CELL_PX.w) * 100_003 + Math.floor(y / LABEL_CELL_PX.h);
      if (!special && (taken.has(cell) || taken.has(cell + 100_003) || taken.has(cell - 100_003))) continue;
      taken.add(cell);
      drawn++;
      // Diamond nodes (libraries, as code calls them) have their names in a fixed-width font.
      const library = nodeLook(n).shape === "diamond";
      top.push({
        type: "text",
        text: n.node.name,
        x,
        y: y + (library ? 6 : 5),
        font: `${library ? "11px" : "11.5px"} ${library ? p.fontMono : p.fontUi}`,
        align: "center",
        baseline: "top",
        color: p.fg,
        halo: { color: p.canvas, width: 3.5 },
        alpha: (dim ? 0.16 : near || (q && match(n)) ? 1 : 0.82) * scene.fade(n),
      });
    }
  }

  // Link labels, only around what is focused.
  for (const l of scene.links) {
    if (!l.visible || !l.link.label || !scene.linkDrawn(l, s.lookAt)) continue;
    if (!(l === s.selectedLink || l === s.hoverLink || (focus && (l.a === focus || l.b === focus)))) continue;
    const seg = scene.segment(l);
    if (!seg) continue;
    const x = (seg.x1 + seg.x2) / 2;
    const y = (seg.y1 + seg.y2) / 2;
    top.push({ type: "text", text: l.link.label, x, y, font: `${SMALL_TEXT_PX}px ${p.fontMono}`, align: "center", baseline: "middle", color: p.fg, halo: { color: p.canvas, width: 4 }, alpha: 1 });
  }

  for (const g of groups) {
    const box = scene.groupLabel(g);
    const named = scene.groupLabelShown(g) || g === s.selectedGroup || g === s.hoverGroup;
    if (named && box.x + box.w > -SCREEN_MARGIN_PX && box.x < right && box.y + box.h > -SCREEN_MARGIN_PX && box.y < bottom) groupLabelShapes(top, scene, p, g, looks.get(g)!);
  }
  // Counts like names: among a few bundles all of them; among many, the biggest first, up to a
  // budget, never one over another (the hit test then only finds those: `countsDrawn`).
  if (closed.length <= COUNTS_ALWAYS) {
    for (const { b, seg, alpha } of closed) bundleCountShapes(top, p, seg, b, alpha, bundleLook);
    scene.countsDrawn(null);
  } else {
    const drawn = new Set<SceneBundle>();
    const taken = new Set<number>();
    for (const c of [...closed].sort((x, y) => y.b.links.length - x.b.links.length || (x.b === s.hoverBundle ? -1 : 0))) {
      if (drawn.size >= COUNT_BUDGET && c.b !== s.hoverBundle) break;
      const x = (c.seg.x1 + c.seg.x2) / 2;
      const y = (c.seg.y1 + c.seg.y2) / 2;
      if (Math.hypot(c.seg.x2 - c.seg.x1, c.seg.y2 - c.seg.y1) < COUNT_MIN_PX) continue;
      const cell = Math.floor(x / COUNT_CELL_PX) * 100_003 + Math.floor(y / COUNT_CELL_PX);
      if (taken.has(cell) && c.b !== s.hoverBundle) continue;
      taken.add(cell);
      drawn.add(c.b);
      bundleCountShapes(top, p, c.seg, c.b, c.alpha, bundleLook);
    }
    scene.countsDrawn(drawn);
  }

  // Round handles at both ends of the selected link, to reconnect it.
  if (s.selectedLink?.visible && !s.reconnecting) {
    const seg = scene.segment(s.selectedLink);
    if (seg) {
      top.push({ type: "circle", x: seg.x1, y: seg.y1, r: LINK_END_PX, paint: both(p.canvas, p.accent, 2.2) });
      top.push({ type: "circle", x: seg.x2, y: seg.y2, r: LINK_END_PX, paint: both(p.canvas, p.accent, 2.2) });
    }
  }

  if (s.reconnecting) {
    const r = s.reconnecting;
    const fixed = r.end === "a" ? r.link.b : r.link.a;
    preview(top, scene, p, scene.sx(fixed), scene.sy(fixed), r.to, r.x, r.y, 6);
  }

  if (!s.hideHandles) {
    const withHandles = [s.hoverNode, s.selectedNode].filter((n, i, all): n is SimNode => !!n && n.visible && all.indexOf(n) === i);
    for (const n of withHandles) handleShapes(top, scene, p, n);
  }

  if (s.linking) {
    const l = s.linking;
    preview(top, scene, p, scene.sx(l.from), scene.sy(l.from), l.to, l.x, l.y, 4);
  }
  return { base, top };
}

/** How a group is drawn: its colour, and how strongly. */
interface GroupLook {
  color: string;
  /** Opacity of the outline and the name (the fill is much lighter). */
  alpha: number;
  width: number;
}

/** Picks a group's look: highlighted when selected or hovered, faded when something else is focused. */
function groupLook(s: DrawState, g: SceneGroup, otherFocus: boolean): GroupLook {
  const p = s.palette;
  if (g === s.selectedGroup) return { color: p.accent, alpha: 1, width: 2 };
  if (g === s.hoverGroup) return { color: p.fg, alpha: 0.75, width: 1.6 };
  return { color: p.muted, alpha: otherFocus || s.selectedGroup ? 0.3 : 0.6, width: 1.2 };
}

/**
 * One group's circle (screen centre and radius): a transparent disc with an outline, dashed when
 * nested. `fillOnly`: its rim is out of sight (the canvas is inside it), only the tint shows.
 */
function groupShapes(out: FlatShape[], { x, y, r }: { x: number; y: number; r: number }, depth: number, look: GroupLook, fillOnly = false) {
  out.push({ type: "circle", x, y, r, paint: filled(look.color, look.alpha * 0.08) });
  if (!fillOnly) out.push({ type: "circle", x, y, r, paint: stroked(look.color, look.width, look.alpha, depth ? [4, 3] : null) });
}

/** A group's name, in a pill on the top of its circle. */
function groupLabelShapes(out: Shape[], scene: Scene, p: Palette, g: SceneGroup, look: GroupLook) {
  const box = scene.groupLabel(g);
  const pill = { type: "box" as const, x: box.x + box.w / 2, y: box.y + box.h / 2, hw: box.w / 2, hh: box.h / 2, radius: box.h / 2, angle: 0 };
  out.push({ ...pill, paint: filled(p.canvas) });
  out.push({ ...pill, paint: stroked(look.color, 1, look.alpha) });
  const color = look.color === p.muted ? p.fg : look.color;
  out.push({ type: "text", text: g.group.name, x: pill.x, y: pill.y + 0.5, font: `600 ${SMALL_TEXT_PX}px ${p.fontUi}`, align: "center", baseline: "middle", color, halo: null, alpha: look.alpha });
}

/** A dashed line from a point to the pointer or to a target node (ring around it). */
function preview(out: Shape[], scene: Scene, p: Palette, fx: number, fy: number, to: SimNode | null, x: number, y: number, dot: number) {
  const tx = to ? scene.sx(to) : x;
  const ty = to ? scene.sy(to) : y;
  out.push({ type: "segment", x1: fx, y1: fy, x2: tx, y2: ty, round: false, paint: stroked(p.accent, 2, 1, [6, 4]) });
  if (to) out.push({ type: "circle", x: tx, y: ty, r: scene.rs(to) + 7, paint: stroked(p.accent, 2) });
  else out.push({ type: "circle", x: tx, y: ty, r: dot, paint: filled(p.accent) });
}

/** The colour of a link kind, by its tone. */
function linkColor(p: Palette, kind: LinkKindView): string {
  return kind.tone === "rule" ? p.depend : kind.tone === "plain" ? p.use : p.inherit;
}

/** A bundle's colour: its kind's colour, or neutral when it mixes kinds. */
function bundleColor(p: Palette, b: SceneBundle, look: (b: SceneBundle) => LinkKindView | null): string {
  const only = look(b);
  return only ? linkColor(p, only) : p.muted;
}

/** The unit vector from a line's start to its end, and its length. */
function direction({ x1, y1, x2, y2 }: Ends): { ux: number; uy: number; len: number } {
  const len = Math.hypot(x2 - x1, y2 - y1) || 1;
  return { ux: (x2 - x1) / len, uy: (y2 - y1) / len, len };
}

/**
 * A bundle's line: one thick line (thicker with more links) in its colour, and an arrow head.
 * Its count goes in the top layer ({@link bundleCountShapes}).
 */
function bundleShapes(out: FlatShape[], p: Palette, seg: Ends, b: SceneBundle, alpha: number, look: (b: SceneBundle) => LinkKindView | null) {
  const { ux, uy } = direction(seg);
  const only = look(b);
  const color = only ? linkColor(p, only) : p.muted;
  const width = 1.4 + Math.sqrt(b.links.length) * 1.1;
  const head = 6 + width * 0.8;
  const dash = only?.line === "dashed" ? ([8, 6] as const) : only?.line === "dotted" ? ([2, 5] as const) : null;
  // Round ends would fill a thick line's gaps (the processor's canvas draws them, the graphics card does not): dashes get flat ends.
  out.push({ type: "segment", x1: seg.x1, y1: seg.y1, x2: seg.x2 - ux * head, y2: seg.y2 - uy * head, round: !dash, paint: stroked(color, width, alpha, dash) });
  out.push({ type: "triangle", x: seg.x2, y: seg.y2, ux, uy, length: head, spread: head * 0.55, paint: filled(color, alpha) });
}

/** The number of links of a bundle, in a small disc in the middle of its line. */
function bundleCountShapes(out: Shape[], p: Palette, seg: Ends, b: SceneBundle, alpha: number, look: (b: SceneBundle) => LinkKindView | null) {
  const x = (seg.x1 + seg.x2) / 2;
  const y = (seg.y1 + seg.y2) / 2;
  const shown = Math.max(alpha, 0.2);
  out.push({ type: "circle", x, y, r: 10, paint: both(p.canvas, bundleColor(p, b, look), 1.5, shown) });
  out.push({ type: "text", text: String(b.links.length), x, y: y + 0.5, font: `600 ${SMALL_TEXT_PX}px ${p.fontUi}`, align: "center", baseline: "middle", color: p.fg, halo: null, alpha: shown });
}

/** The dashes of a line style. */
function dashes(kind: LinkKindView): readonly [number, number] | null {
  if (kind.line === "dotted") return [...DOTTED_DASH];
  if (kind.line === "dashed") return [5, 4];
  return null;
}

/**
 * One link: its kind's line (solid, dashed, dotted) and head (a hollow or filled triangle, an
 * open chevron, a diamond, or none), coloured by its tone.
 */
function linkShapes(out: FlatShape[], p: Palette, seg: Ends, kind: LinkKindView, alpha: number, touch: boolean) {
  const { ux, uy, len } = direction(seg);
  const color = linkColor(p, kind);
  // A link a few pixels long has no room for a head: a plain line says enough.
  const head = len >= HEAD_MIN_PX ? kind.head : "none";
  const triangle = head === "hollow" || head === "filled";
  const length = triangle ? 10 : head === "diamond" ? 12 : 6;
  const width = (kind.tone === "plain" ? 1 : 1.4) + (touch ? 0.7 : 0);
  const inset = triangle || head === "diamond";
  const ex = inset ? seg.x2 - ux * length : seg.x2;
  const ey = inset ? seg.y2 - uy * length : seg.y2;
  out.push({ type: "segment", x1: seg.x1, y1: seg.y1, x2: ex, y2: ey, round: false, paint: stroked(color, width, alpha, dashes(kind)) });
  if (triangle) {
    const fill = head === "filled" ? color : p.canvas;
    out.push({ type: "triangle", x: seg.x2, y: seg.y2, ux, uy, length, spread: 4.6, paint: both(fill, color, width, alpha) });
  } else if (head === "diamond") {
    const half = length / 2 / Math.SQRT2;
    const angle = Math.atan2(uy, ux) + Math.PI / 4;
    out.push({ type: "box", x: seg.x2 - (ux * length) / 2, y: seg.y2 - (uy * length) / 2, hw: half, hh: half, radius: 0, angle, paint: both(p.canvas, color, width, alpha) });
  } else if (head === "open") {
    out.push({ type: "chevron", x: seg.x2, y: seg.y2, ux, uy, length: 6, spread: 3.2, paint: stroked(color, width, alpha) });
  }
}

/**
 * One node, shaped by its kind (its own shape, else the shape of the kind it is like), with an optional
 * ring and a dot when it has comments.
 */
function nodeShapes(out: FlatShape[], scene: Scene, p: Palette, n: SimNode, alpha: number, ring: string | null, look: NodeKindView) {
  const x = scene.sx(n);
  const y = scene.sy(n);
  const r = scene.rs(n);
  const color = colorOf(scene, p, n);
  switch (look.shape) {
    case "square":
      out.push({ type: "box", x, y, hw: r * 0.9, hh: r * 0.9, radius: r * 0.12, angle: 0, paint: filled(color, alpha) });
      break;
    case "triangle":
      out.push({ type: "triangle", x, y: y - r * 1.15, ux: 0, uy: -1, length: r * 2, spread: r * 1.15, paint: filled(color, alpha) });
      break;
    case "rounded":
      out.push({ type: "box", x, y, hw: r, hh: r, radius: r * 0.38, angle: 0, paint: filled(color, alpha) });
      break;
    case "diamond": {
      // A diamond: a square turned a quarter of a right angle, its corners 1.3 r away.
      const half = (r * 1.3) / Math.SQRT2;
      out.push({ type: "box", x, y, hw: half, hh: half, radius: 0, angle: Math.PI / 4, paint: filled(color, alpha) });
      break;
    }
    case "hexagon":
      out.push({ type: "hexagon", x, y, r: r * 1.12, paint: filled(color, alpha) });
      break;
    case "ring":
      out.push({ type: "circle", x, y, r, paint: both(p.canvas, color, 2.2, alpha) });
      break;
    case "halo":
      out.push({ type: "circle", x, y, r, paint: filled(color, alpha) });
      out.push({ type: "circle", x, y, r: r + 3.2, paint: stroked(color, 1.4, alpha * 0.7) });
      break;
    default:
      out.push({ type: "circle", x, y, r, paint: filled(color, alpha) });
  }
  if (n.node.comments.length && r >= DOT_MIN_PX) out.push({ type: "circle", x: x + r * 0.85, y: y - r * 0.85, r: 3.3, paint: both(p.accent, p.canvas, 1.5, alpha) });
  if (ring) out.push({ type: "circle", x, y, r: r + RING_GAP_PX, paint: stroked(ring, 2, Math.max(alpha, 0.95)) });
}

/**
 * A folded group's disc (see `state/scene/folds.ts`): a ring in its members' colour with a
 * second ring inside, and how many nodes it hides written in the middle.
 */
function foldShapes(out: FlatShape[], top: Shape[], scene: Scene, p: Palette, n: SimNode, alpha: number, ring: string | null) {
  const x = scene.sx(n);
  const y = scene.sy(n);
  const r = scene.rs(n);
  const color = colorOf(scene, p, n);
  out.push({ type: "circle", x, y, r, paint: both(p.canvas, color, 2.4, alpha) });
  out.push({ type: "circle", x, y, r: r * 0.74, paint: stroked(color, 1.2, alpha * 0.7, [4, 3]) });
  if (r >= DOT_MIN_PX) {
    top.push({ type: "text", text: String(n.fold!.members.length), x, y: y + 0.5, font: `600 ${Math.min(14, Math.max(9, r * 0.7)).toFixed(1)}px ${p.fontUi}`, align: "center", baseline: "middle", color: p.fg, halo: null, alpha });
  }
  if (ring) out.push({ type: "circle", x, y, r: r + RING_GAP_PX, paint: stroked(ring, 2, Math.max(alpha, 0.95)) });
}

/** How much of each shape's size the picture's disc takes, and how far down its middle is (in radii). */
const PICTURE_FIT: Partial<Record<string, { r: number; dy: number }>> = {
  triangle: { r: 0.55, dy: 0.25 },
  diamond: { r: 0.62, dy: 0 },
  square: { r: 0.8, dy: 0 },
  ring: { r: 0.78, dy: 0 },
};

/** A node's picture (its first attached image) inside its shape, or nothing. */
export function pictureShape(scene: Scene, n: SimNode, alpha: number, kinds: Kinds | undefined): ImageShape | null {
  const key = pictureOf(n.node.attachments);
  if (!key || !n.visible) return null;
  const r = scene.rs(n);
  const fit = PICTURE_FIT[nodeKindOf(kinds, n.node).shape ?? "circle"] ?? { r: 0.86, dy: 0 };
  if (r * fit.r < PICTURE_MIN_PX) return null;
  return { type: "image", key, x: scene.sx(n), y: scene.sy(n) + r * fit.dy, r: r * fit.r, alpha };
}

/** The small arrow button beside a node: drag from it to create a link. */
function handleShapes(out: Shape[], scene: Scene, p: Palette, n: SimNode) {
  const h = handlePosition(scene, n);
  out.push({ type: "circle", x: h.x, y: h.y, r: 6.5, paint: both(p.accent, p.canvas, 1.5) });
  out.push({ type: "segment", x1: h.x - 2.6, y1: h.y, x2: h.x + 2.6, y2: h.y, round: false, paint: stroked(p.canvas, 1.6) });
  out.push({ type: "chevron", x: h.x + 3, y: h.y, ux: 1, uy: 0, length: 2.4, spread: 2.4, paint: stroked(p.canvas, 1.6) });
}
